/**
 * OrdersRealtimeProvider
 * 
 * Single realtime subscription for all order changes.
 * Mounted at AppShellContent level (authenticated routes only).
 * 
 * Responsibilities:
 * - One channel per table (orders)
 * - Event classification and targeted cache patching
 * - Connection state tracking with fallback polling
 * 
 * Dependencies: user.id, queryClient only
 * NO: t(), theme, route params, sidebar state
 * 
 * @see docs/REALTIME_ARCHITECTURE.md for full design rationale
 */

import { ReactNode, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeContext, ConnectionState } from "./RealtimeContext";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { Order } from "@/types/order";

// Channel name for unified sync
const CHANNEL_NAME = 'orders-unified-sync';

// Fallback polling interval when connection is degraded (60s safety net)
const FALLBACK_POLLING_INTERVAL = 60 * 1000;

type ChangeType = 'insert' | 'status' | 'note' | 'session' | 'delete' | 'minor';

interface OrdersRealtimeProviderProps {
  children: ReactNode;
}

export function OrdersRealtimeProvider({ children }: OrdersRealtimeProviderProps) {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');

  // Main subscription effect
  useEffect(() => {
    // No user = degraded state (not authenticated)
    if (!user?.id) {
      setConnectionState('degraded');
      return;
    }

    // Cleanup previous channel if it exists (e.g., user changed)
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(CHANNEL_NAME)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => handleOrderChange(payload, queryClient)
      )
      .subscribe((status, err) => {
        // Connection state stability - avoid flapping
        if (status === 'SUBSCRIBED') {
          setConnectionState('live');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[OrdersRealtime] Channel error/timeout:', err);
          setConnectionState('degraded');
        }
        // Otherwise: keep prior state (connecting → connecting)
      });

    channelRef.current = channel;

    // Cleanup on unmount or user change
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, queryClient]);

  // Fallback polling when degraded
  useEffect(() => {
    if (connectionState !== 'degraded') return;

    const interval = setInterval(() => {
      // Only refresh critical queries - safety net
      queryClient.invalidateQueries({ queryKey: ['today-orders'] });
      queryClient.invalidateQueries({ queryKey: ['active-delivery-link'] });
    }, FALLBACK_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [connectionState, queryClient]);

  return (
    <RealtimeContext.Provider value={{ connectionState }}>
      {children}
    </RealtimeContext.Provider>
  );
}

/**
 * Handles realtime order changes with event classification and cache patching.
 * Never throws - silently logs errors to prevent UI breakage.
 */
function handleOrderChange(
  payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>,
  queryClient: ReturnType<typeof useQueryClient>
) {
  try {
    const eventType = payload.eventType;
    const newOrder = payload.new as Order | null;
    const oldOrder = payload.old as Partial<Order> | null;

    // DELETE events - invalidate lists, don't try to patch
    if (eventType === 'DELETE') {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['today-orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
      return;
    }

    // For INSERT and UPDATE, we need the new order data
    if (!newOrder || !newOrder.id) {
      console.warn('[OrdersRealtime] Missing new order data:', payload);
      return;
    }

    const changeType = classifyChange(eventType, oldOrder, newOrder);

    // Apply changes based on classification
    switch (changeType) {
      case 'insert':
        // Don't prepend - just invalidate lists to avoid filter/ordering issues
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['today-orders'] });
        queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
        queryClient.invalidateQueries({ queryKey: ['hasAnyOrders'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        // Patch detail cache (for if user navigates to it quickly)
        patchOrderDetail(newOrder, queryClient);
        break;

      case 'status':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // Invalidate aggregates that depend on status
        queryClient.invalidateQueries({ queryKey: ['today-kpis'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        break;

      case 'note':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // No aggregate invalidation needed for notes
        break;

      case 'session':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // Invalidate delivery-related queries
        queryClient.invalidateQueries({ queryKey: ['delivery-stats'] });
        queryClient.invalidateQueries({ queryKey: ['session-orders'] });
        break;

      case 'minor':
        // Only patch detail - don't refresh lists for minor changes
        patchOrderDetail(newOrder, queryClient);
        break;

      default:
        patchOrderDetail(newOrder, queryClient);
    }
  } catch (err) {
    // Never throw inside realtime handler - UI still works via polling/refetch
    console.error('[OrdersRealtime] Error handling change:', err);
  }
}

/**
 * Classifies the type of change based on eventType and field diffs.
 */
function classifyChange(
  eventType: 'INSERT' | 'UPDATE' | 'DELETE',
  oldOrder: Partial<Order> | null,
  newOrder: Order
): ChangeType {
  if (eventType === 'INSERT') return 'insert';
  if (eventType === 'DELETE') return 'delete';

  // For UPDATE, compare specific fields
  if (oldOrder?.order_status !== newOrder.order_status) return 'status';
  if (oldOrder?.internal_notes !== newOrder.internal_notes) return 'note';
  if (oldOrder?.assigned_to !== newOrder.assigned_to) return 'session';

  return 'minor';
}

/**
 * Patches the individual order detail cache.
 */
function patchOrderDetail(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setQueryData(['order', order.id], order);
}

/**
 * Patches the today-orders list cache (flat Order[] array).
 * Only updates if order exists in cache - doesn't add new orders.
 */
function patchTodayOrders(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setQueryData<Order[]>(['today-orders'], (old) => {
    // Guard: Must be array
    if (!Array.isArray(old)) return old;
    
    // Only update if order exists in cache
    const exists = old.some(o => o.id === order.id);
    if (!exists) return old;
    
    return old.map(o => o.id === order.id ? order : o);
  });
}

/**
 * Patches paginated orders cache (object with orders array).
 * Iterates over all cached pages/filter combinations.
 * Only updates if order exists - doesn't add new orders.
 */
function patchPaginatedOrders(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  // setQueriesData iterates over all matching query keys
  queryClient.setQueriesData<{ orders: Order[]; totalCount: number }>(
    { queryKey: ['orders'] },
    (old) => {
      // Guard: Must have expected shape
      if (!old || !Array.isArray(old.orders)) return old;
      
      // Only update if order exists in this page's cache
      const exists = old.orders.some(o => o.id === order.id);
      if (!exists) return old;
      
      return {
        ...old,
        orders: old.orders.map(o => o.id === order.id ? order : o),
      };
    }
  );
}
